/* eslint-disable react/no-danger */
import { FC } from "react";

const BASE_URL = process.env.NEXT_PUBLIC_URL || "https://flexibooking.sk";

export const OrganizationSchema: FC = () => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "FlexiBooking.sk",
        alternateName: "FlexiBooking",
        url: BASE_URL,
        logo: `${BASE_URL}/logo.png`,
        description:
          "Premium white-label booking system for businesses. Customizable reservation software with your brand, your design, your rules.",
        sameAs: [],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "Sales",
          email: "info@flexibooking.sk",
        },
      }),
    }}
  />
);

export const SoftwareApplicationSchema: FC = () => {
  const currentYear = new Date().getFullYear();
  const priceValidUntil = new Date(currentYear + 1, 11, 31).toISOString();

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "FlexiBooking",
          applicationCategory: "BusinessApplication",
          applicationSubCategory: "Booking & Reservation Software",
          operatingSystem: "Web Browser",
          softwareVersion: "2.0",
          offers: {
            "@type": "Offer",
            price: "29.00",
            priceCurrency: "EUR",
            description: "Starting price for single-operator businesses (monthly subscription)",
            url: BASE_URL,
            availability: "https://schema.org/InStock",
            priceValidUntil,
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: "29.00",
              priceCurrency: "EUR",
              unitText: "MONTH",
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            bestRating: "5",
            worstRating: "1",
            ratingCount: "24",
          },
          description:
            "Premium and blazingly fast custom booking system for hair salons, beauty studios, and healthcare facilities. Full visual customization, white-label interface, and sub-500ms performance.",
          screenshot: `${BASE_URL}/assets/admin-calendar.png`,
          featureList: [
            "Full CSS customization",
            "Sub-500ms widget performance",
            "White-label interface (no branding)",
            "SMS & Email automation",
            "Payment gateway integration (Stripe, PayPal)",
            "Multi-staff scheduling",
            "Resource management",
            "Real-time availability",
            "Mobile-responsive design",
            "GDPR compliant (EU servers)",
            "One-line script integration",
            "WordPress, Webflow, Wix compatible",
            "Admin panel with analytics",
            "Calendar management",
            "Multi-language support (11 languages)",
          ],
          about: {
            "@type": "Thing",
            name: "Online Booking Systems",
            description:
              "Software for managing appointments, bookings, and reservations for service-based businesses",
          },
          audience: {
            "@type": "Audience",
            audienceType:
              "Small and medium businesses in beauty, wellness, and healthcare sectors",
          },
        }),
      }}
    />
  );
};

export const WebSiteSchema: FC = () => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "FlexiBooking.sk",
        url: BASE_URL,
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${BASE_URL}/?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      }),
    }}
  />
);

interface BreadcrumbSchemaProps {
  locale: string;
  platform?: string;
}

export const BreadcrumbSchema: FC<BreadcrumbSchemaProps> = ({ locale, platform }) => {
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${BASE_URL}/${locale}`,
    },
  ];

  if (platform) {
    const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
    items.push({
      "@type": "ListItem",
      position: 2,
      name: `${platformName} Integration`,
      item: `${BASE_URL}/${locale}/${platform}`,
    });
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: items,
        }),
      }}
    />
  );
};

interface ProductSchemaProps {
  platform: string;
  locale: string;
}

export const ProductSchema: FC<ProductSchemaProps> = ({ platform, locale }) => {
  const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: `FlexiBooking for ${platformName}`,
          description: `White-label booking widget integration for ${platformName}. Seamlessly integrate a customizable reservation system into your ${platformName} website.`,
          image: `${BASE_URL}/og_image.png`,
          brand: {
            "@type": "Brand",
            name: "FlexiBooking",
          },
          offers: {
            "@type": "Offer",
            url: `${BASE_URL}/${locale}/${platform}`,
            priceCurrency: "EUR",
            price: "0",
            availability: "https://schema.org/InStock",
          },
        }),
      }}
    />
  );
};
